import { createAvatar } from '@dicebear/core';
import { avataaars } from '@dicebear/collection';

// --- Mappings for DiceBear avataaars collection ---
// Top (Hair/Hat) mapping
export const TOP_MAPPING = {
  NoHair: 'shavedSides',
  Eyepatch: 'eyepatch',
  Hat: 'hat',
  Hijab: 'hijab',
  Turban: 'turban',
  WinterHat1: 'winterHat1',
  WinterHat2: 'winterHat02',
  WinterHat3: 'winterHat03',
  WinterHat4: 'winterHat04',
  LongHairBigHair: 'bigHair',
  LongHairBob: 'bob',
  LongHairBun: 'bun',
  LongHairCurly: 'curly',
  LongHairCurvy: 'curvy',
  LongHairDreads: 'dreads',
  LongHairFrida: 'frida',
  LongHairFro: 'fro',
  LongHairFroBand: 'froBand',
  LongHairNotTooLong: 'longButNotTooLong',
  LongHairShavedSides: 'shavedSides',
  LongHairMiaWallace: 'miaWallace',
  LongHairStraight: 'straight01',
  LongHairStraight2: 'straight02',
  LongHairStraightStrand: 'straightAndStrand',
  ShortHairDreads01: 'dreads01',
  ShortHairDreads02: 'dreads02',
  ShortHairFrizzle: 'frizzle',
  ShortHairShaggyMullet: 'shaggyMullet',
  ShortHairShortCurly: 'shortCurly',
  ShortHairShortFlat: 'shortFlat',
  ShortHairShortRound: 'shortRound',
  ShortHairShortWaved: 'shortWaved',
  ShortHairSides: 'sides',
  ShortHairTheCaesar: 'theCaesar',
  ShortHairTheCaesarSidePart: 'theCaesarAndSidePart'
};

export const ACCESSORIES_MAPPING = {
  Blank: [],
  Kurt: 'kurt',
  Prescription01: 'prescription01',
  Prescription02: 'prescription02',
  Round: 'round',
  Sunglasses: 'sunglasses',
  Wayfarers: 'wayfarers'
};

export const CLOTHING_MAPPING = {
  BlazerShirt: 'blazerAndShirt',
  BlazerSweater: 'blazerAndSweater',
  CollarSweater: 'collarAndSweater',
  GraphicShirt: 'graphicShirt',
  Hoodie: 'hoodie',
  Overall: 'overall',
  ShirtCrewNeck: 'shirtCrewNeck',
  ShirtScoopNeck: 'shirtScoopNeck',
  ShirtVNeck: 'shirtVNeck'
};

// Colors must be hex codes without #
export const SKIN_COLORS = {
  Tanned: 'fd9841',
  Yellow: 'f8d25c',
  Pale: 'ffdbb4',
  Light: 'edb98a',
  Brown: 'd08b5b',
  DarkBrown: 'ae5d29',
  Black: '614335'
};

export const HAIR_COLORS = {
  Auburn: 'a55728',
  Black: '2c1b18',
  Blonde: 'b58143',
  BlondeGolden: 'd6b370',
  Brown: '724133',
  BrownDark: '4a312c',
  PastelPink: 'f59797',
  Blue: '0000f0',
  Platinum: 'ecdcbf',
  Red: 'c93305',
  SilverGray: 'e8e1e1'
};

export const CLOTHING_COLORS = {
  Black: '262e33',
  Blue01: '65c9ff',
  Blue02: '5199e4',
  Blue03: '25557c',
  Gray01: 'e6e6e6',
  Gray02: '929598',
  Heather: '3c4f5c',
  PastelBlue: 'b1e2ff',
  PastelGreen: 'a7ffc4',
  PastelOrange: 'ffdeb5',
  PastelRed: 'ffafb9',
  PastelYellow: 'ffffb1',
  Pink: 'ff488e',
  Red: 'ff5c5c',
  White: 'ffffff'
};

export const BACKGROUND_COLORS = {
  Transparent: 'transparent',
  Circle: 'Circle', // Special case logic
  White: 'ffffff',
  Blue: 'b6e3f4',
  Purple: 'c0aede',
  Yellow: 'fff6cc',
  Gray: 'e2e8f0',
};

// Simplified Options Structure for UI Generation
export const UI_GROUPS = {
  'Base': [
    { id: 'avatarStyle', label: 'Background', type: 'select', options: Object.keys(BACKGROUND_COLORS) },
    { id: 'skinColor', label: 'Skin Tone', type: 'color', options: SKIN_COLORS },
  ],
  'Head': [
    { id: 'top', label: 'Hair / Hat', type: 'select', options: Object.keys(TOP_MAPPING) },
    { id: 'hairColor', label: 'Hair Color', type: 'color', options: HAIR_COLORS },
    { id: 'facialHair', label: 'Facial Hair', type: 'select', options: ['Blank', 'BeardMedium', 'BeardLight', 'BeardMajestic', 'MoustacheFancy', 'MoustacheMagnum'] },
    { id: 'facialHairColor', label: 'Facial Hair Color', type: 'color', options: HAIR_COLORS },
  ],
  'Face': [
    { id: 'eyes', label: 'Eyes', type: 'select', options: ['Close', 'Cry', 'Default', 'Dizzy', 'EyeRoll', 'Happy', 'Hearts', 'Side', 'Squint', 'Surprised', 'Wink', 'WinkWacky'] },
    { id: 'eyebrows', label: 'Eyebrows', type: 'select', options: ['Angry', 'AngryNatural', 'Default', 'DefaultNatural', 'FlatNatural', 'RaisedExcited', 'SadConcerned', 'UnibrowNatural', 'UpDown', 'UpDownNatural'] },
    { id: 'mouth', label: 'Mouth', type: 'select', options: ['Concerned', 'Default', 'Disbelief', 'Eating', 'Grimace', 'Sad', 'ScreamOpen', 'Serious', 'Smile', 'Tongue', 'Twinkle', 'Vomit'] },
  ],
  'Clothes & Accessories': [
    { id: 'clothing', label: 'Clothing', type: 'select', options: Object.keys(CLOTHING_MAPPING) },
    { id: 'clothingColor', label: 'Clothes Color', type: 'color', options: CLOTHING_COLORS },
    { id: 'accessories', label: 'Eyewear', type: 'select', options: Object.keys(ACCESSORIES_MAPPING) },
  ]
};

export const DEFAULT_OPTS = {
  avatarStyle: 'Transparent',
  top: 'ShortHairShortFlat',
  accessories: 'Blank',
  hairColor: 'Brown',
  facialHair: 'Blank',
  facialHairColor: 'Brown',
  clothing: 'Hoodie',
  clothingColor: 'PastelBlue',
  eyes: 'Happy',
  eyebrows: 'Default',
  mouth: 'Smile',
  skinColor: 'Light'
};

// Helper to convert PascalCase to camelCase
const toCamelCase = (str) => {
  return str.charAt(0).toLowerCase() + str.slice(1);
};

/**
 * Generate random avatar options
 * @returns {Object} Random avatar options object
 */
export function generateRandomAvatarOptions() {
  const newOpts = { ...DEFAULT_OPTS };

  Object.keys(UI_GROUPS).forEach(group => {
    UI_GROUPS[group].forEach(field => {
      if (field.id === 'avatarStyle') return;

      let keys;
      if (field.type === 'select') {
        keys = field.options;
      } else {
        keys = Object.keys(field.options);
      }
      const randomVal = keys[Math.floor(Math.random() * keys.length)];
      newOpts[field.id] = randomVal;
    });
  });

  // Keep transparent background for gallery cards
  newOpts.avatarStyle = 'Transparent';

  return newOpts;
}

/**
 * Create avatar SVG from options
 * @param {Object} opts - Avatar options
 * @returns {string} SVG string
 */
export function createAvatarSvg(opts) {
  // Background logic
  let bg = [];
  if (opts.avatarStyle !== 'Transparent' && opts.avatarStyle !== 'Circle') {
    if (BACKGROUND_COLORS[opts.avatarStyle]) {
      bg = [BACKGROUND_COLORS[opts.avatarStyle]];
    }
  }

  return createAvatar(avataaars, {
    seed: 'custom-seed',
    // Mappings
    top: TOP_MAPPING[opts.top] ? [TOP_MAPPING[opts.top]] : ['shortFlat'],
    accessories: (ACCESSORIES_MAPPING[opts.accessories] && ACCESSORIES_MAPPING[opts.accessories].length > 0)
      ? [ACCESSORIES_MAPPING[opts.accessories]] : [],
    accessoriesProbability: 100,
    clothing: CLOTHING_MAPPING[opts.clothing] ? [CLOTHING_MAPPING[opts.clothing]] : ['hoodie'],

    // Colors
    hairColor: [HAIR_COLORS[opts.hairColor]],
    facialHairColor: [HAIR_COLORS[opts.facialHairColor]],
    facialHairProbability: 100,
    skinColor: [SKIN_COLORS[opts.skinColor]],
    clothesColor: [CLOTHING_COLORS[opts.clothingColor]],
    backgroundColor: bg,

    // Simple CamelCase
    eyes: [toCamelCase(opts.eyes)],
    eyebrows: [toCamelCase(opts.eyebrows)],
    mouth: [toCamelCase(opts.mouth)],
    facialHair: [toCamelCase(opts.facialHair)],
  }).toString();
}

/**
 * Generate a random avatar (convenience function)
 * @returns {Object} Object containing svg string and options
 */
export function generateRandomAvatar() {
  const options = generateRandomAvatarOptions();
  const svg = createAvatarSvg(options);
  return { svg, options };
}
